import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';
import { saveDeviceConfigurationInstance } from 'modules/settings/pods/settings-devices/settings-devices-configuration/service/settings-devices-configuration.service';
import { ErroneousCombinationsType } from 'modules/settings/pods/settings-devices/settings-devices-configuration/service/device-layout-config.api';
import { DeviceConfigurationInfo } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-list/settings-devices-configuration-list.model';
import {
    DeviceConfigurationInstanceProperty,
    DeviceLayoutDependencies,
} from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-layout/settings-devices-configuration-layout.model';
import { MAX_NUMBER_OF_ITERATIONS } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration.constant';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfigurationDetailContext } from './use-configuration-detail-context.hook';

import { PROPERTY_STATUS } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-configuration-layout/constants';
import {
    DeviceConfigurationValidity,
    ErroneousCombinationsValidationsType,
    PropertyValidationType,
} from '../device-configuration.type';

type DependencyTargetValue = DeviceLayoutDependencies['targetValue'];

type DeviceDependencyModelState = { disabled: boolean; hidden: boolean; forcedValue?: DependencyTargetValue };

const useDependencyState = () => {
    const { deviceConfigurationInstance, sectionDetails, dependencies, layoutSections } =
        useConfigurationDetailContext();
    const [userModifiedProperties, setUserModifiedProperties] = useState<Set<string>>(new Set());

    const deviceProperties = useMemo(
        () => sectionDetails?.headers.flatMap((header) => header.properties) ?? [],
        [sectionDetails]
    );

    const dependencyState = useMemo(() => {
        const updatedProperties = deviceConfigurationInstance?.properties || {};
        const sections = layoutSections?.sections || [];
        const state: Record<string, DeviceDependencyModelState> = {};

        const statusActions = {
            [PROPERTY_STATUS.DISABLED]: (
                targetState: DeviceDependencyModelState,
                targetValue?: DependencyTargetValue
            ) => {
                targetState.disabled = true;
                if (targetValue != null) {
                    targetState.forcedValue = targetValue;
                }
            },
            [PROPERTY_STATUS.HIDDEN]: (targetState: DeviceDependencyModelState) => {
                targetState.hidden = true;
            },
            [PROPERTY_STATUS.ENABLED]: (
                targetState: DeviceDependencyModelState,
                targetValue?: DependencyTargetValue
            ) => {
                targetState.disabled = false;
                if (targetValue != null) {
                    targetState.forcedValue = targetValue;
                }
            },
        };

        const applyDependenciesToTarget = (
            targetState: DeviceDependencyModelState,
            applicableDependencies: typeof dependencies
        ) => {
            targetState.disabled = false;
            targetState.hidden = false;

            const statusPriority = Object.values(PROPERTY_STATUS);
            const dependencyWithTargetValue = applicableDependencies.find(
                (dep) => dep.targetValue != null && dep.targetValue !== ''
            );

            for (const status of statusPriority) {
                for (const dep of applicableDependencies.filter((d) => d.status === status)) {
                    if (statusActions[dep.status]) {
                        const valueToUse = dependencyWithTargetValue?.targetValue ?? dep.targetValue;
                        statusActions[dep.status](targetState, valueToUse);
                    }
                }
            }
        };

        const handleDependencies = (
            target: string,
            applicableDependencies: typeof dependencies,
            changedPropertiesInIteration: Set<string>
        ) => {
            if (!state[target]) {
                state[target] = { disabled: false, hidden: false };
                changedPropertiesInIteration.add(target);
            }

            const prevPropertyState = { ...state[target] };
            applyDependenciesToTarget(state[target], applicableDependencies);

            if (
                prevPropertyState.disabled !== state[target].disabled ||
                prevPropertyState.hidden !== state[target].hidden ||
                prevPropertyState.forcedValue !== state[target].forcedValue
            ) {
                changedPropertiesInIteration.add(target);
            }
        };

        const getCurrentPropertyValue = (propertyName: string): unknown => {
            const propertyState = state[propertyName];

            if (userModifiedProperties.has(propertyName) && updatedProperties[propertyName] !== undefined) {
                return updatedProperties[propertyName];
            }

            if (propertyState?.forcedValue !== undefined) {
                return propertyState.forcedValue;
            }

            if (updatedProperties[propertyName] !== undefined) {
                return updatedProperties[propertyName];
            }

            return deviceProperties.find((devProp) => devProp.name === propertyName)?.defaultValue;
        };

        const isTargetASection = (targetName: string): boolean => {
            const isMainSection = sections.some((section) => section.name === targetName);
            const isSubSection = sections
                .flatMap((section) => section.subSections)
                .some((subSection) => subSection.name === targetName);
            return isMainSection || isSubSection;
        };

        const dependenciesByTarget = dependencies.reduce(
            (acc, dep) => {
                if (!acc[dep.target]) {
                    acc[dep.target] = [];
                }
                acc[dep.target].push(dep);
                return acc;
            },
            {} as Record<string, typeof dependencies>
        );

        let hasChanges = true;
        let currentIteration = 0;
        const maxIterations = MAX_NUMBER_OF_ITERATIONS;

        while (hasChanges && currentIteration < maxIterations) {
            currentIteration++;
            const changedPropertiesInIteration = new Set<string>();

            Object.entries(dependenciesByTarget).forEach(([target, targetDependencies]) => {
                const applicableDependencies = targetDependencies.filter((dep) => {
                    const sourceValue = getCurrentPropertyValue(dep.source);
                    return (
                        sourceValue === dep.sourceValue ||
                        (Array.isArray(sourceValue) && sourceValue.includes(dep.sourceValue))
                    );
                });

                if (applicableDependencies.length > 0) {
                    handleDependencies(target, applicableDependencies, changedPropertiesInIteration);

                    if (isTargetASection(target)) {
                        const section = sections.find((sec) => sec.name === target);
                        if (section) {
                            handleDependencies(section.name, applicableDependencies, changedPropertiesInIteration);
                        }
                    }
                }
            });

            hasChanges = changedPropertiesInIteration.size > 0;
        }

        return { state, getCurrentPropertyValue };
    }, [deviceConfigurationInstance, sectionDetails, dependencies, userModifiedProperties]);

    return {
        dependencyState,
        userModifiedProperties,
        setUserModifiedProperties,
        deviceProperties,
    };
};

export const useErroneousCombinations = () => {
    const {
        layoutSections,
        sectionDetails,
        deviceConfigurationInstance,
        deviceConfigurationValidation,
        setDeviceConfigurationValidation,
    } = useConfigurationDetailContext();

    const deviceProperties = useMemo(
        () => sectionDetails?.headers.flatMap((header) => header.properties) ?? [],
        [sectionDetails]
    );

    const getErroneousConditionCode = useCallback((condition: string) => {
        switch (condition) {
            case 'LESS_THAN':
                return 'greater_than';
            case 'GREATER_THAN':
                return 'less_than';
            default:
                return condition.toLowerCase();
        }
    }, []);

    const getPropertyDisplayName = useCallback(
        (propertyName: string) =>
            deviceProperties.find((prop) => prop.name === propertyName)?.displayName || propertyName,
        [deviceProperties]
    );

    const hasFieldValidationError = useCallback(
        (validation: PropertyValidationType) =>
            validation?.validity.state === 'error' &&
            validation?.validity.type?.some((type: string) => type !== 'custom'),
        []
    );

    const evaluateCondition = useCallback((condition: string, sourceValue: number, targetValue: number) => {
        const conditionMap = {
            LESS_THAN: sourceValue < targetValue,
            GREATER_THAN: sourceValue > targetValue,
            EQUALS: sourceValue === targetValue,
        } as const;

        return conditionMap[condition as keyof typeof conditionMap] ?? false;
    }, []);

    const createCombinationError = useCallback(
        (code: string, targetPropertyName: string, sourcePropertyName: string) => ({
            code,
            targetProperty: getPropertyDisplayName(targetPropertyName),
            sourceProperty: sourcePropertyName,
        }),
        [getPropertyDisplayName]
    );

    const updateCombinationsArray = useCallback(
        (
            existingCombinations: PropertyValidationType['erroneousCombinations'],
            newCombination: PropertyValidationType['erroneousCombinations'][number],
            findCondition: (errCombi: { code: string; targetProperty: string; sourceProperty: string }) => boolean
        ) => {
            const updatedCombinations = [...existingCombinations];
            const existingIndex = existingCombinations.findIndex(findCondition);

            if (existingIndex >= 0) {
                updatedCombinations[existingIndex] = newCombination;
            } else {
                updatedCombinations.push(newCombination);
            }

            return updatedCombinations;
        },
        []
    );

    const handleErrorCondition = useCallback(
        (
            combination: ErroneousCombinationsType,
            currentValidation: DeviceConfigurationValidity,
            hasSourceError: boolean
        ) => {
            const { condition, sourceProperty, targetProperty } = combination;
            const updates: Partial<DeviceConfigurationValidity> = {};

            if (!hasSourceError) {
                const currentSourceValidation = currentValidation[sourceProperty];
                const existingCombinations = currentSourceValidation?.erroneousCombinations || [];

                const newCombination = createCombinationError(condition.toLowerCase(), targetProperty, sourceProperty);

                const updatedCombinations = updateCombinationsArray(
                    existingCombinations,
                    newCombination,
                    (combo) =>
                        combo.sourceProperty === sourceProperty &&
                        combo.targetProperty === getPropertyDisplayName(targetProperty)
                );

                updates[sourceProperty] = {
                    validity: { state: 'error', type: ['custom'] },
                    erroneousCombinations: updatedCombinations,
                };
            }

            return updates;
        },
        [createCombinationError, getErroneousConditionCode, getPropertyDisplayName, updateCombinationsArray]
    );

    const clearPropertyError = useCallback(
        (
            currentValidation: PropertyValidationType,
            hasFieldError: boolean,
            filterCondition: (errCombi: ErroneousCombinationsValidationsType) => boolean
        ): PropertyValidationType | null => {
            if (
                currentValidation?.validity.state === 'valid' ||
                !currentValidation?.validity.type?.includes('custom')
            ) {
                return null;
            }

            const updatedCombinations = currentValidation.erroneousCombinations.filter(filterCondition);

            if (updatedCombinations.length === 0 && !hasFieldError) {
                return {
                    validity: { state: 'valid', type: [] },
                    erroneousCombinations: [],
                };
            } else if (updatedCombinations.length !== currentValidation.erroneousCombinations.length) {
                return {
                    validity: { state: 'error', type: ['custom'] },
                    erroneousCombinations: updatedCombinations,
                };
            }

            return null;
        },
        []
    );

    const handleClearCondition = useCallback(
        (
            combination: ErroneousCombinationsType,
            currentValidation: DeviceConfigurationValidity,
            hasSourceError: boolean
        ) => {
            const { sourceProperty, targetProperty } = combination;
            const updates: Partial<DeviceConfigurationValidity> = {};

            const sourceUpdate = clearPropertyError(
                currentValidation[sourceProperty],
                hasSourceError,
                (errCombi) =>
                    !(
                        errCombi.sourceProperty === sourceProperty &&
                        errCombi.targetProperty === getPropertyDisplayName(targetProperty)
                    )
            );

            if (sourceUpdate) {
                updates[sourceProperty] = sourceUpdate;
            }

            return updates;
        },
        [clearPropertyError, getPropertyDisplayName]
    );

    const processErroneousCombination = useCallback(
        (
            combination: ErroneousCombinationsType,
            properties: DeviceConfigurationInstanceProperty,
            currentValidation: DeviceConfigurationValidity
        ) => {
            const { condition, sourceProperty, targetProperty } = combination;
            const sourceValue = Number.parseFloat(properties[sourceProperty] as string);
            const targetValue = Number.parseFloat(properties[targetProperty] as string);

            if (Number.isNaN(sourceValue) || Number.isNaN(targetValue)) {
                return {};
            }

            const currentSourceValidation = currentValidation[sourceProperty];

            const hasSourceFieldError = hasFieldValidationError(currentSourceValidation) ?? false;

            const isErrorCondition = evaluateCondition(condition, sourceValue, targetValue);

            if (isErrorCondition) {
                return handleErrorCondition(combination, currentValidation, hasSourceFieldError);
            } else {
                return handleClearCondition(combination, currentValidation, hasSourceFieldError);
            }
        },
        [hasFieldValidationError, evaluateCondition, handleErrorCondition, handleClearCondition]
    );

    const checkErroneousCombinations = useCallback(() => {
        const erroneousCombinations = layoutSections?.erroneousCombinations || [];
        const properties = deviceConfigurationInstance?.properties || {};

        const allUpdates = erroneousCombinations.reduce<Partial<DeviceConfigurationValidity>>(
            (acc, combination) => ({
                ...acc,
                ...processErroneousCombination(combination, properties, deviceConfigurationValidation),
            }),
            {}
        );

        if (Object.keys(allUpdates).length > 0) {
            const newValidationState: DeviceConfigurationValidity = {
                ...deviceConfigurationValidation,
                ...(allUpdates as DeviceConfigurationValidity),
            };
            setDeviceConfigurationValidation(newValidationState);
        }
    }, [layoutSections, deviceConfigurationInstance, deviceConfigurationValidation, processErroneousCombination]);

    useEffect(() => {
        checkErroneousCombinations();
    }, [layoutSections, deviceConfigurationInstance]);

    return {
        deviceConfigurationValidation,
        setDeviceConfigurationValidation,
        checkErroneousCombinations,
    };
};

export const useDeviceConfiguration = () => {
    const { setDeviceConfigurationInstance, hasConfigChanged, setHasConfigChanged, deviceConfigurationInstance } =
        useConfigurationDetailContext();

    const { dependencyState, setUserModifiedProperties, deviceProperties } = useDependencyState();

    const setDeviceConfiguration = <K extends keyof DeviceConfigurationInfo>(
        propertyName: K,
        value: DeviceConfigurationInfo[K]
    ) => {
        if (deviceConfigurationInstance) {
            const updatedInstance = {
                ...deviceConfigurationInstance,
                [propertyName]: value,
            };

            setDeviceConfigurationInstance(updatedInstance);
            setHasConfigChanged(true);
        }
    };

    const setDeviceConfigurationProperty = (propertyName: string, value: unknown) => {
        const property = deviceProperties.find((prop) => prop.name === propertyName);
        if (deviceConfigurationInstance && setDeviceConfigurationInstance) {
            if (Array.isArray(value) && value.length === 1 && property?.contentType === 'BOOLEAN') {
                deviceConfigurationInstance.properties[propertyName] = value[0] === 'true' ? '1' : '0';
            } else if (deviceConfigurationInstance.properties) {
                deviceConfigurationInstance.properties[propertyName] = value;
            }
            setDeviceConfigurationInstance({ ...deviceConfigurationInstance });
            setHasConfigChanged(true);
            setUserModifiedProperties((prev) => new Set(prev).add(propertyName));
        }
    };

    const isPropertyDisabled = (propertyName: string) => dependencyState.state[propertyName]?.disabled;
    const isPropertyHidden = (propertyName: string) => dependencyState.state[propertyName]?.hidden;
    const isSectionDisabled = (sectionName: string) => dependencyState.state[sectionName]?.disabled;
    const isSectionHidden = (sectionName: string) => dependencyState.state[sectionName]?.hidden;

    const { mutateAsync: saveConfigurationMutation, isPending: isLoading } = usePocMutation({
        mutationFn: (instance: typeof deviceConfigurationInstance) =>
            executeRequest(() => saveDeviceConfigurationInstance(instance)),
        onSuccess: () => {
            setHasConfigChanged(false);
            updateQuery(settingsKeys.configurations());
        },
    });

    const saveDeviceConfiguration = useCallback(
        () => saveConfigurationMutation(deviceConfigurationInstance),
        [deviceConfigurationInstance, saveConfigurationMutation]
    );

    return {
        deviceConfigurationInstance,
        hasConfigChanged,
        setDeviceConfiguration,
        setDeviceConfigurationProperty,
        isPropertyDisabled,
        isPropertyHidden,
        getPropertyValue: dependencyState.getCurrentPropertyValue,
        saveDeviceConfiguration,
        isSectionDisabled,
        isSectionHidden,
        isLoading,
    };
};
